package dev.lingvo.shared.screenshots.design.derive

import dev.lingvo.shared.screenshots.doc.ScreenshotDesign
import dev.lingvo.shared.screenshots.doc.GroupLayer
import dev.lingvo.shared.screenshots.doc.Layer
import dev.lingvo.shared.screenshots.doc.TextContent
import dev.lingvo.shared.screenshots.doc.TextLayer
import dev.lingvo.shared.screenshots.doc.TextTransform
import dev.lingvo.shared.screenshots.render.RenderInputs
import dev.lingvo.shared.screenshots.render.effectiveLayers
import dev.lingvo.shared.screenshots.text.DefaultFontResolver
import dev.lingvo.shared.screenshots.text.FitRequest
import dev.lingvo.shared.screenshots.text.FitResult
import dev.lingvo.shared.screenshots.text.Scripts
import dev.lingvo.shared.screenshots.text.TextFitter

/** A per-layer refusal retains the target device and locale rather than silently lowering the font floor. */
data class DerivedTextFit(val device: String, val locale: String, val layerId: String, val fit: FitResult?) {
    val accepted: Boolean get() = fit != null && fit !is FitResult.Refused
}

/** Measurement is explicit and shared with the renderer; geometry derivation never invents font metrics. */
fun fitDerivedDevice(design: ScreenshotDesign, device: String, inputs: RenderInputs): List<DerivedTextFit> {
    val board = design.boards.first { it.deviceClass == device }
    var offset = 0
    return buildList {
        for (page in board.pages) {
            for (layer in visibleTextLayers(effectiveLayers(page, board, inputs.locale, design.schemaVersion))) {
                val local = (layer.content as? TextContent.SlotCaption)?.slot ?: layer.base.roleSlot
                val text = (layer.content as? TextContent.Literal)?.text ?: inputs.captions(layer.content, offset + local)
                val style = layer.style
                val fit = text?.let {
                    val script = inputs.scripts.scriptFor(inputs.locale, it)
                    val stack = if (inputs.fonts === DefaultFontResolver) DefaultFontResolver.stack(style.role, style.family,
                        design.brand, inputs.templateFamilies[style.role], script, Scripts.hasEmoji(it), style.weight, style.italic)
                    else inputs.fonts.stack(style.role, style.family, design.brand, inputs.templateFamilies[style.role], script, Scripts.hasEmoji(it))
                    TextFitter.fit(FitRequest(it, inputs.locale, layer.base.box.w, layer.base.box.h, stack,
                        style.weight, style.italic, layer.fit, style.lineHeight, style.letterSpacingEm, style.align, style.vAlign,
                        style.transform == TextTransform.UPPERCASE, style.quoted), inputs.measurer, inputs.segmenter)
                }
                add(DerivedTextFit(device, inputs.locale, layer.base.id, fit))
            }
            offset += page.slotIds.size
        }
    }
}

/** A hidden ancestor suppresses its entire subtree in the renderer, including fit checks. */
private fun visibleTextLayers(layers: List<Layer>): List<TextLayer> = layers.flatMap { layer ->
    when {
        layer.base.hidden || layer.base.opacity == 0.0 -> emptyList()
        layer is TextLayer -> listOf(layer)
        layer is GroupLayer -> visibleTextLayers(layer.children)
        else -> emptyList()
    }
}
